/**
 * Отчёт режима «Отчёт» (ADR-0011) — РАЗРЕШЁННАЯ спецификация: сервер
 * (`ai_agent/report.py`) проверил блоки и исполнил привязки к данным, сюда
 * приходят готовые строки. Фронт только рисует каталогом компонентов.
 */

import type { AiWidgetSpec } from '../widgets/spec';

export interface ReportSource {
  kind: 'scenario' | 'sql' | 'inline';
  ref?: string;
  params?: Record<string, unknown>;
  at?: string;
}

export type Cell = string | number | boolean | null;

export interface TextBlock {
  type: 'text';
  title: string | null;
  markdown: string;
}

export interface KpiTile {
  label: string;
  value: Cell;
  unit: string | null;
  note: string | null;
  tone: 'neutral' | 'ok' | 'warning' | 'critical';
  source: ReportSource;
}

export interface KpiBlock {
  type: 'kpi';
  title: string | null;
  items: KpiTile[];
  /** Числа блока — демо-данные (synthetic_demo): пометку рисует панель. */
  demo?: boolean;
}

export interface TableBlock {
  type: 'table';
  title: string | null;
  columns: { key: string; label: string; unit: string | null }[];
  rows: Cell[][];
  total: number;
  truncated: boolean;
  source: ReportSource;
  note: string | null;
  demo?: boolean;
}

export interface ChartBlock {
  type: 'chart';
  kind: 'bar' | 'line' | 'area';
  title: string | null;
  unit: string[];
  labels: string[];
  series: { name: string; values: (number | null)[] }[];
  source: ReportSource;
  note: string | null;
  demo?: boolean;
}

export interface MapBlock {
  type: 'map';
  title: string | null;
  contours: number[];
  color_by: 'crop' | 'workStatus' | 'quarantine' | 'landCategory' | 'water' | 'values';
  /** Значения по id контура: числа (слой `values`) или статусы слоя (6al.62). */
  values_by_contour?: Record<string, number | string>;
  value_label?: string | null;
  value_unit?: string | null;
  source: ReportSource;
  note: string | null;
  demo?: boolean;
}

export interface LinkBlock {
  type: 'link';
  kind: 'link';
  page: string;
  title: string;
  href: string;
  label: string;
  region?: string | null;
}

/** Живой виджет экрана (`backend/ai_agent/widgets.py`): данные читает сам. */
export interface WidgetBlock {
  type: 'widget';
  widget: string;
  title: string;
  params: Record<string, string | number>;
  href?: string | null;
  page?: string | null;
  page_title?: string | null;
  region?: string | null;
  scope?: string | null;
  scope_parts?: AiWidgetSpec['scope_parts'];
  public?: boolean;
  outside_scope?: boolean;
  note?: string | null;
}

export type ReportBlock = TextBlock | KpiBlock | TableBlock | ChartBlock | MapBlock | LinkBlock | WidgetBlock;

export interface Report {
  version: 1;
  title: string;
  subtitle: string | null;
  generated_at: string;
  as_of: string | null;
  include_demo: boolean;
  /** Есть блоки на демо-данных (synthetic_demo); у отчётов, собранных до agroai-wo4, поля нет. */
  demo?: boolean;
  blocks: ReportBlock[];
  /** Исходная спецификация с привязками — для «Обновить». */
  spec: Record<string, unknown>;
  /** Версия отчёта (agroai-6al.60): растёт с каждой правкой («Версия N»). */
  revision?: number;
  /** Отчёт сохранён, правка стала его текущей версией. */
  saved_report?: { id: string; version: number };
  /** Что сделала последняя правка словами. */
  edit?: { done: string[] };
}

export const isReport = (value: unknown): value is Report =>
  typeof value === 'object' &&
  value !== null &&
  (value as Report).version === 1 &&
  Array.isArray((value as Report).blocks) &&
  typeof (value as Report).title === 'string';
